import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Link2, Sparkles } from "lucide-react";

import { Button } from "@/components/primitives/button";
import { Input } from "@/components/primitives/input";
import { Label } from "@/components/primitives/label";
import { useToast } from "@/hooks/useToast";
import { copyTextToClipboard } from "@/lib/clipboard";
import { canonicalizeAlias, isAliasShape } from "@/lib/meetVanity";
import { ApiError } from "@/server/apiError";
import { claimAlias, getMyMeeting } from "@/server/services/meetingsService";
import { useSubscriptionState } from "@/zustand/SubscriptionState";

type View =
  | { status: "loading" }
  | { status: "loadError" }
  | { status: "claimed"; alias: string }
  | { status: "upsell" }
  | { status: "form" };

/**
 * Vanity-alias CLAIM + MANAGE page (route `/meet/vanity`).
 *
 * On mount fetches the caller's personal meeting (`getMyMeeting`):
 *   - already has an alias → show the link (+ copy) and note that changing it is
 *     coming soon;
 *   - Free user with no alias → an upsell (premium is required to claim);
 *   - paid user with no alias → the claim form.
 *
 * Premium is enforced by the backend (403 `AliasPremiumRequired`); the FE tier
 * check only decides whether to show the upsell instead of the form up-front —
 * the 403 is still handled as the hard gate. Charset is validated client-side
 * for instant feedback, but the BACKEND is the authority (reserved words, race
 * on the same name, etc.).
 */
export default function VanityManagePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const plan = useSubscriptionState((s) => s.subscription.plan);
  const isFreeTier = plan === "free";

  const [view, setView] = useState<View>({ status: "loading" });
  const [aliasInput, setAliasInput] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Synchronous double-submit guard (state updates are async).
  const submittingRef = useRef(false);

  async function loadMine() {
    setView({ status: "loading" });
    try {
      const meeting = await getMyMeeting();
      if (meeting) {
        setView({ status: "claimed", alias: meeting.alias });
      } else {
        setView({ status: isFreeTier ? "upsell" : "form" });
      }
    } catch {
      setView({ status: "loadError" });
    }
  }

  useEffect(() => {
    loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCopy(alias: string) {
    const ok = await copyTextToClipboard(`${window.location.origin}/${alias}`);
    toast({
      duration: 2000,
      description: ok ? t("meetVanity.copied") : t("meetVanity.copyFailed"),
      ...(ok ? {} : { variant: "destructive" as const }),
    });
  }

  function mapClaimError(error: unknown): "handled" | string {
    if (!(error instanceof ApiError)) {
      return t("meetVanity.errorGeneric");
    }
    const code = (error.data as { title?: string } | undefined)?.title ?? "";
    if (error.status === 403 || code.includes("AliasPremiumRequired")) {
      // Hard premium gate — swap to the upsell regardless of the FE tier guess.
      setView({ status: "upsell" });
      return "handled";
    }
    if (error.status === 409 && code.includes("AliasAlreadyClaimed")) {
      // The caller already owns an alias (claimed elsewhere/concurrently) —
      // re-fetch and show it rather than a stale form.
      loadMine();
      return "handled";
    }
    if (error.status === 409) {
      return t("meetVanity.errorTaken");
    }
    if (error.status === 400 && code.includes("AliasReserved")) {
      return t("meetVanity.errorReserved");
    }
    if (error.status === 400) {
      return t("meetVanity.errorInvalid");
    }
    return t("meetVanity.errorGeneric");
  }

  async function handleClaim(e: React.FormEvent) {
    e.preventDefault();
    if (submittingRef.current) {
      return;
    }
    const alias = canonicalizeAlias(aliasInput.trim());
    if (!isAliasShape(alias)) {
      setInlineError(t("meetVanity.errorInvalid"));
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setInlineError(null);
    try {
      const result = await claimAlias({ alias });
      setView({ status: "claimed", alias: result.alias });
      toast({ duration: 2500, description: t("meetVanity.claimSuccess") });
    } catch (error) {
      const mapped = mapClaimError(error);
      if (mapped !== "handled") {
        setInlineError(mapped);
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  const containerClass =
    "flex h-dvh w-full flex-col items-center justify-center gap-4 bg-background p-6 text-center font-DMSans text-foreground";

  if (view.status === "loading") {
    return (
      <div className={containerClass}>
        <p className="text-sm opacity-80">{t("meetVanity.loading")}</p>
      </div>
    );
  }

  if (view.status === "loadError") {
    return (
      <div className={containerClass}>
        <h1 className="text-xl font-semibold">{t("meetVanity.loadFailed")}</h1>
        <Button onClick={loadMine}>{t("meetVanity.retry")}</Button>
      </div>
    );
  }

  if (view.status === "claimed") {
    const displayLink = `${window.location.host}/${view.alias}`;
    return (
      <div className={containerClass}>
        <Link2 className="size-10 text-primary" aria-hidden="true" />
        <h1 className="text-xl font-semibold">{t("meetVanity.title")}</h1>
        <div className="flex flex-col items-center gap-2">
          <span className="text-sm opacity-80">
            {t("meetVanity.yourLinkLabel")}
          </span>
          <code className="rounded-md bg-foreground/10 px-3 py-1 text-sm">
            {displayLink}
          </code>
        </div>
        <Button onClick={() => handleCopy(view.alias)} className="min-w-44">
          {t("meetVanity.copy")}
        </Button>
        <p className="max-w-md text-xs opacity-60">
          {t("meetVanity.changeComingSoon")}
        </p>
      </div>
    );
  }

  if (view.status === "upsell") {
    return (
      <div className={containerClass}>
        <Sparkles className="size-10 text-primary" aria-hidden="true" />
        <h1 className="text-xl font-semibold">
          {t("meetVanity.premiumTitle")}
        </h1>
        <p className="max-w-md text-sm opacity-80">
          {t("meetVanity.premiumDescription")}
        </p>
        <Button onClick={() => navigate("/subscription")} className="min-w-44">
          {t("meetVanity.upgrade")}
        </Button>
      </div>
    );
  }

  // view.status === "form"
  return (
    <div className={containerClass}>
      <Link2 className="size-10 text-primary" aria-hidden="true" />
      <h1 className="text-xl font-semibold">{t("meetVanity.claimTitle")}</h1>
      <p className="max-w-md text-sm opacity-80">
        {t("meetVanity.claimDescription")}
      </p>
      <form
        onSubmit={handleClaim}
        className="flex w-full max-w-sm flex-col items-stretch gap-2 text-left"
      >
        <Label htmlFor="vanity-alias">{t("meetVanity.inputLabel")}</Label>
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-sm opacity-70">
            {window.location.host}/
          </span>
          <Input
            id="vanity-alias"
            value={aliasInput}
            onChange={(e) => {
              setAliasInput(e.target.value);
              if (inlineError) {
                setInlineError(null);
              }
            }}
            placeholder={t("meetVanity.placeholder")}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            aria-invalid={inlineError ? true : undefined}
            aria-describedby="vanity-alias-hint"
          />
        </div>
        <p id="vanity-alias-hint" className="text-xs opacity-60">
          {t("meetVanity.charsetHint")}
        </p>
        {inlineError && (
          <p role="alert" className="text-sm text-destructive">
            {inlineError}
          </p>
        )}
        <Button type="submit" disabled={submitting} className="mt-2">
          {submitting ? t("meetVanity.claiming") : t("meetVanity.claim")}
        </Button>
      </form>
    </div>
  );
}
